import React, { FunctionComponent, useEffect, useState } from 'react';
import { Button, Card, Spinner } from '../../../ui';
import { Helmet } from 'react-helmet';
import { Link, useHistory, useLocation } from 'react-router-dom';
import { useServiceErrorHandler } from '../../../../hooks/useServiceErrorHandler';
import * as emailManager from '../../../../services/email';
import { EmailUnsubscribeLink } from '../../../../services/models/email-unsubscribe-link';

const EmailUnsubscribePage: FunctionComponent = () => {
  const history = useHistory();
  const { search } = useLocation();
  const handleServiceError = useServiceErrorHandler();

  // Query Params
  const searchParams = new URLSearchParams(search);
  const emailId = searchParams.get('e');
  const verificationCode = searchParams.get('c');

  // Local state machine mirroring the retired emailUnsubscribeLink slice.
  const [emailUnsubscribeLink, setEmailUnsubscribeLink] = useState<EmailUnsubscribeLink>({});

  useEffect(() => {
    if (!verificationCode || !emailId) {
      history.push('/');
    }
  }, [verificationCode, emailId, history]);

  useEffect(() => {
    if (!verificationCode || !emailId) {
      return;
    }

    let cancelled = false;
    setEmailUnsubscribeLink(current => ({ ...current, isFetching: true }));
    emailManager
      .getEmailUnsubscribeLink(verificationCode, emailId)
      .then(link => {
        if (!cancelled) setEmailUnsubscribeLink(current => ({ ...current, ...link }));
      })
      .catch(error => handleServiceError(error))
      .finally(() => {
        if (!cancelled) setEmailUnsubscribeLink(current => ({ ...current, isFetching: false }));
      });

    return () => {
      cancelled = true;
    };
  }, [emailId, verificationCode, handleServiceError]);

  const handleUnsubscribe = async () => {
    if (!verificationCode || !emailId) {
      return;
    }

    setEmailUnsubscribeLink(current => ({ ...current, isUnsubscribing: true }));
    try {
      const success = await emailManager.unsubscribeFromSocialDigest(verificationCode, emailId);
      setEmailUnsubscribeLink(current => ({
        ...current,
        isUnsubscribing: false,
        isCompleted: success,
      }));
    } catch (error) {
      handleServiceError(error, () =>
        setEmailUnsubscribeLink(current => ({ ...current, isUnsubscribing: false }))
      );
    }
  };

  // Fetching the Email Unsubscribe link
  if (emailUnsubscribeLink.isFetching === undefined || emailUnsubscribeLink.isFetching) {
    return (
      <div>
        <Card>
          <Card.Body>
            <h1 className="flex items-center justify-center gap-2 text-lg font-medium text-gray-500 my-4">
              <Spinner animation="grow"></Spinner>
              <span>Loading...</span>
            </h1>
          </Card.Body>
        </Card>
      </div>
    );
  }

  // Problems fetching the Email Unsubscribe link
  if (emailUnsubscribeLink.isFetching === false && !emailUnsubscribeLink.emailAddress) {
    return (
      <div>
        <Card>
          <Card.Body>
            <h1 className="text-2xl font-bold text-gray-900 text-center mb-6">
              Oops! Something went wrong.
            </h1>
            <Link to="/">Return to Newslit</Link>
          </Card.Body>
        </Card>
      </div>
    );
  }

  if (emailUnsubscribeLink.isFetching === false && !emailUnsubscribeLink.receiveDigest) {
    return (
      <div>
        <Card>
          <Card.Body>
            <h1 className="text-2xl font-bold text-gray-900 text-center mb-6">
              Already Unsubscribe
            </h1>
            <p className="text-sm text-gray-500 mb-4">
              You're already unsubscribed from your Newslit Social Brief.
            </p>
            <Link to="/">Return to Newslit</Link>
          </Card.Body>
        </Card>
      </div>
    );
  }

  // Email Unsubscribe Completed successfully
  if (
    emailUnsubscribeLink &&
    emailUnsubscribeLink.isFetching === false &&
    emailUnsubscribeLink.isCompleted
  ) {
    return (
      <div>
        <Card>
          <Card.Body>
            <h1 className="text-2xl font-bold text-gray-900 text-center mb-6">
              Unsubscribe Successful
            </h1>
            <p className="text-sm text-gray-500 mb-4">
              You will no longer receive a daily email related to your Social Brief.
            </p>
            <Link to="/">Return to Newslit</Link>
          </Card.Body>
        </Card>
      </div>
    );
  }

  return (
    <div>
      <Helmet>
        <title>Unsubscribe from Newslit Email</title>
      </Helmet>

      <Card>
        <Card.Body>
          <h2 className="text-2xl font-bold text-gray-900 mb-6">
            Would you like to unsubscribe from these emails?
          </h2>

          <p className="text-sm text-gray-500 mb-4">
            Your email address <strong>{emailUnsubscribeLink.emailAddress}</strong> will stop
            receiving:
          </p>

          <ul className="mb-4 list-disc pl-8">
            <li>Daily Newslit Social Digest at {emailUnsubscribeLink.digestHour}:00</li>
          </ul>

          <div className="mt-4">
            <Button
              disabled={!emailUnsubscribeLink.emailAddress || emailUnsubscribeLink.isUnsubscribing}
              variant="purple"
              className="mr-1"
              onClick={() => handleUnsubscribe()}
            >
              {emailUnsubscribeLink.isUnsubscribing ? 'Unsubscribing...' : 'Unsubscribe'}
            </Button>
            <Button
              variant="white"
              disabled={!emailUnsubscribeLink.emailAddress || emailUnsubscribeLink.isUnsubscribing}
              as={Link}
              to={'/'}
            >
              Cancel
            </Button>
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

export default EmailUnsubscribePage;
